import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { TranslocoModule } from '@ngneat/transloco';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TUI_TEXTFIELD_LABEL_OUTSIDE } from '@taiga-ui/core';
import { DataTablesModule } from 'angular-datatables';
import { BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { environment } from 'src/environment/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LeftSideMenuComponent } from './core/components/left-side-menu/left-side-menu.component';
import { SpinnerComponent } from './core/components/spinner/spinner.component';
import { MSALInterceptorConfigFactory } from './core/factories/msal-interceptor-config.factory';
import { GreenInterceptor } from './core/interceptors/green.interceptor';
import { LicenseInterceptor } from './core/interceptors/license.interceptor';
import { MsalOverridedInterceptor } from './core/interceptors/msal-overrided.interceptor';
import { SpinnerInterceptor } from './core/interceptors/spinner.interceptor';
import { AuthActions } from './core/store/actions';
import { GetUserDetailsEffects } from './core/store/effects';
import { authReducer } from './core/store/reducer';
import { AppState } from './core/store/state';
import { TranslocoRootModule } from './core/transloco/transloco-root.module';
import { LetDirective } from './core/utils/let-directive';
import { CounterpartiesModule } from './modules/counterparties/counterparties.module';
import { DialogsModule } from './modules/dialogs/dialogs.module';
import { DocumentsModule } from './modules/documents/documents.module';
import { LocalizationModule } from './modules/localization/localization.module';
import { LoginComponent } from './modules/login/components/login.component';
import { NotificationsModule } from './modules/notifications/notifications.module';
import { QuestionsModule } from './modules/questions/questions.module';
import { TemplatesModule } from './modules/templates/templates.module';
import { PipesModule } from './shared/pipes/pipes.module';
import { RouteReuseStrategy } from '@angular/router';
import { SplitButtonModule } from 'primeng/splitbutton';
import { AngularMaterialModule } from 'src/app/modules/angular-material.module';
import { DocGenerationNotificationComponent } from './core/components/notifications/doc-generation-notification/doc-generation-notification.component';
import { DocumentLoaderNotificationComponent } from './core/components/notifications/document-loader-notification/document-loader-notification.component';
import { ReportNotificationComponent } from './core/components/notifications/report-notification/report-notification.component';
import { WorkspaceDocumentsLoaderNotificationComponent } from './core/components/notifications/workspace-documents-loader-notification/workspace-documents-loader-notification.component';
import { CustomReuseStrategy } from './core/route/custom-reuse.strategy';
import { AccessRestrictedModule } from './modules/access-restricted/access-restricted.module';
import { DocumentGenerationModule } from './modules/document-generation/document-generation.module';
import { DocGenerationSnackbarComponent } from './modules/notifications/components/doc-generation-snackbar/doc-generation-snackbar.component';
import { SnackbarComponent } from './modules/notifications/components/snackbar/snackbar.component';
import { WorkspaceAddDocsSnackbarComponent } from './modules/notifications/components/workspace-add-docs-snackbar/workspace-add-docs-snackbar.component';
import { WorkspaceCreationSnackbarComponent } from './modules/notifications/components/workspace-creation-snackbar/workspace-creation-snackbar.component';
import { WorkspacesModule } from './modules/workspaces/workspaces.module';
import { IconSetupService } from './shared/services/icon-setup.service';
import { SharedModule } from './shared/shared.module';
import { TAIGA_UI_MODULES } from './shared/taiga.module';
export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(environment.msalConfig); // todo
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: environment.loginRequest,
  };
}

export function initIcons(iconService: IconSetupService) {
  return () => iconService.init();
}

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};

@NgModule({
  declarations: [
    AppComponent,
    SpinnerComponent,
    LeftSideMenuComponent,
    LoginComponent,
    DocumentLoaderNotificationComponent,
    ReportNotificationComponent,
    DocGenerationNotificationComponent,
    SnackbarComponent,
    DocGenerationSnackbarComponent,
    WorkspaceDocumentsLoaderNotificationComponent,
    WorkspaceCreationSnackbarComponent,
    WorkspaceAddDocsSnackbarComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    SharedModule,
    SplitButtonModule,
    HttpClientModule,
    LocalizationModule,
    StoreModule.forRoot({ rootState: authReducer }),
    EffectsModule.forRoot([GetUserDetailsEffects]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    MatMenuModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    AccessRestrictedModule,
    DocumentsModule,
    WorkspacesModule,
    DocumentGenerationModule,
    QuestionsModule,
    TemplatesModule,
    TranslocoRootModule,
    TranslocoModule,
    DataTablesModule,
    PipesModule,
    DialogsModule,
    ModalModule.forRoot(),
    LetDirective,
    TAIGA_UI_MODULES,
    MsalModule,
    NotificationsModule,
    CounterpartiesModule,
    AngularMaterialModule,
  ],
  providers: [
    [
      {
        provide: APP_INITIALIZER,
        useFactory: initApp,
        deps: [Store],
        multi: true,
      },
      {
        provide: APP_INITIALIZER,
        useFactory: initIcons,
        deps: [IconSetupService],
        multi: true,
      },
    ],
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true },
    { provide: TUI_TEXTFIELD_LABEL_OUTSIDE, useValue: { labelOutside: false } },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: HTTP_INTERCEPTORS, useClass: MsalOverridedInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LicenseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: GreenInterceptor, multi: true },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    IconSetupService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {
  constructor(private modalService: BsModalService) {
    this.modalService.config.keyboard = true;
    this.modalService.config.backdrop = 'static';
    this.modalService.config.ignoreBackdropClick = true;
  }
}

export function initApp(store$: Store<AppState>) {
  return () => {
    return new Promise<void>(async (resolve) => {
      //Office.initialize = function () {};

      let href = window.location.href;

      if (href.indexOf('clauses?clauseName=') !== -1) {
        let clauseNameToBeFiltered = decodeURI(href.split('clauses?clauseName=')?.[1]);

        if (clauseNameToBeFiltered) {
          store$.dispatch(AuthActions.setClauseNameToBeFiltered({ name: clauseNameToBeFiltered }));
        }
      }

      if (href.indexOf('?code=') !== -1) {
        const codeExpression = decodeURI(href.split('?code=')?.[1]);

        if (codeExpression) {
          store$.dispatch(AuthActions.setAuthCode({ authCode: codeExpression }));
        }
      }

      resolve();
    });
  };
}
